import { ApiTagTypes, innovorderApi } from '../index';
import { ApiRequest, ApiResponse } from '../request.types';
import {
    GetBrandPayload,
    Brand,
    GetBrandGroups,
    ActiveStudentEntities,
    BrandOptions,
    UpdateBrandOptionsPayload,
    GetBrandOptionsResponse,
    DomainNames,
    PaymentConfiguration,
    GetBrandsListResponse,
    GetBrandListV2Payload,
    ToggleBrandFavoritePayload,
    CreateBrandResponse,
    CreateBrandPayload,
    BrandOperationZoneStatus,
    CreateBrandRestaurantPayload,
    CreateBrandRestaurantResponse,
    BrandOptIns,
    DefaultPricingPolicy,
    UpdateDefaultPricingPolicyPayload,
    WhitelistedDomainName,
    UpdateWhitelistedDomainNamesPayload,
    UpdateWhitelistingEnabledPayload,
    BrandRestaurantFamily,
    SetBrandRestaurantFamiliesPayload,
    UpdateBrandPayload,
} from './brand.type';
import { Grant } from '../grant/grant.type';
import { EntranceFee } from '../entranceFee/entranceFee.type';
import { ModuleOrderingFlowAttributes, Restaurant } from '../restaurant/restaurant.type';

const brandApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getBrand: build.query<Brand, ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<Brand>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandOperationZoneStatus: build.query<BrandOperationZoneStatus, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/operation-zone-status`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.BrandRestaurants, ApiTagTypes.OperationZone],
            transformResponse: (response: ApiResponse<BrandOperationZoneStatus>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        updateBrand: build.mutation<Brand, ApiRequest<UpdateBrandPayload>>({
            query: ({ brandId, token, ...body }) => ({
                url: `/brands/${brandId}`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body,
            }),
            invalidatesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<Brand>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandGrants: build.query<Grant[], ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/grants`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.Grant],
            transformResponse: (response: ApiResponse<Grant[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandEntranceFees: build.query<EntranceFee[], ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/entrance_fees`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.EntranceFee],
            transformResponse: (response: ApiResponse<EntranceFee[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandRestaurants: build.query<
            Restaurant[],
            ApiRequest<{ brandId: number; active?: boolean; channels?: number[] }>
        >({
            query: ({ brandId, active, channels, token }) => ({
                url: `/brands/${brandId}/restaurants`,
                method: 'GET',
                params: { active, channels },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.BrandRestaurants],
            transformResponse: (response: ApiResponse<Restaurant[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandGroups: build.query<string[], ApiRequest<GetBrandGroups>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/groups`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<string[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getGroups: build.mutation<string[], ApiRequest<GetBrandGroups>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/groups`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<string[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getRestaurantsForBrand: build.mutation<Restaurant[], ApiRequest<GetBrandGroups>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/restaurants`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<Restaurant[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getActiveStudentsEntities: build.query<ActiveStudentEntities, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/students_entities`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<ActiveStudentEntities>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getWhitelistedCustomerEmailDomainNames: build.query<DomainNames, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/whitelisted_customer_email_domain_names`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<DomainNames>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandPaymentConfiguration: build.query<PaymentConfiguration, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/payment_configuration`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.BrandPaymentConfiguration],
            transformResponse: (response: ApiResponse<PaymentConfiguration>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        updateBrandPaymentConfiguration: build.mutation<
            PaymentConfiguration,
            ApiRequest<Partial<PaymentConfiguration> & { brandId: number }>
        >({
            query: ({ brandId, token, ...data }) => ({
                url: `/brands/${brandId}/payment_configuration`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: data,
            }),
            invalidatesTags: [ApiTagTypes.BrandPaymentConfiguration],
            transformResponse: (response: ApiResponse<PaymentConfiguration>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        countPostPaymentCustomers: build.query<{ count: number }, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/customers/paymentTypes/POSTPAYMENT/count`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<{ count: number }>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandOptions: build.query<GetBrandOptionsResponse, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/options`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<GetBrandOptionsResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        updateBrandOptions: build.mutation<BrandOptions, ApiRequest<UpdateBrandOptionsPayload>>({
            query: ({ brandId, token, options }) => ({
                url: `/brands/${brandId}/options`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: {
                    brandId,
                    options,
                },
            }),
            transformResponse: (response: ApiResponse<BrandOptions>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getBrandsListV2: build.query<GetBrandsListResponse, ApiRequest<GetBrandListV2Payload>>({
            query: (data) => {
                const { offset, limit, search, filter, sortBy, token } = data;
                return {
                    url: `brands/v2/`,
                    method: 'GET',
                    params: { offset, limit, search, ...filter, sortBy: JSON.stringify(sortBy) },
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: (response: ApiResponse<GetBrandsListResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
            providesTags: [ApiTagTypes.BrandsList],
        }),

        getBrandModuleOrderingFlow: build.query<ModuleOrderingFlowAttributes, ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/modules/2?channelId=1`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<ModuleOrderingFlowAttributes>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        updateBrandModuleOrderingFlow: build.mutation<
            ModuleOrderingFlowAttributes,
            ApiRequest<ModuleOrderingFlowAttributes>
        >({
            query: ({ brandId, token, ...data }) => ({
                url: `/brands/${brandId}/modules`,
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: {
                    brandId,
                    moduleId: 2,
                    channelId: 1,
                    module: data,
                },
            }),
            transformResponse: (response: ApiResponse<ModuleOrderingFlowAttributes>) => response.data,
        }),

        toggleBrandFavorites: build.mutation<
            ApiResponse<Record<string, never>>,
            ApiRequest<ToggleBrandFavoritePayload>
        >({
            query: ({ brandId, token, status }) => ({
                url: `brands/favorites`,
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { brandId, status },
            }),
            invalidatesTags: [ApiTagTypes.BrandsList],
            onQueryStarted: async ({ brandId, latestFetchParams }, { dispatch, queryFulfilled }) => {
                if (latestFetchParams === null) return;
                const patchResult = dispatch(
                    brandApi.util.updateQueryData('getBrandsListV2', latestFetchParams, (brandsList) => {
                        const indexToBeChanged = brandsList.brands.map((el) => el.brandId).indexOf(brandId);
                        brandsList.brands[indexToBeChanged].isFavorite =
                            !brandsList.brands[indexToBeChanged].isFavorite;
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        createBrand: build.mutation<CreateBrandResponse, ApiRequest<CreateBrandPayload>>({
            query: ({ token, ...data }) => ({
                url: `brands`,
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: {
                    name: data.name,
                    channels: data.channels,
                    locale: data.locale,
                    currency: data.currency,
                    user: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        phone: data.phone,
                        email: data.email,
                        password: data.password,
                        confirmPassword: data.confirmPassword,
                        ...(data.letUserChoosePassword ? { letUserChoosePassword: true } : {}),
                    },
                },
            }),
            transformResponse: (response: ApiResponse<CreateBrandResponse>) => response.data,
            invalidatesTags: [ApiTagTypes.BrandsList],
        }),
        getBrandOptIns: build.query<BrandOptIns, ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/opt_ins`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<BrandOptIns>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandDefaultPricingPolicy: build.query<DefaultPricingPolicy, ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/default_pricing_policy`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<{ defaultPricingPolicy: DefaultPricingPolicy }>) =>
                response.data.defaultPricingPolicy,
            extraOptions: { maxRetries: 3 },
        }),
        updateBrandDefaultPricingPolicy: build.mutation<
            DefaultPricingPolicy,
            ApiRequest<UpdateDefaultPricingPolicyPayload>
        >({
            query: ({ brandId, token, defaultPricingPolicy }) => ({
                url: `/brands/${brandId}/default_pricing_policy`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { defaultPricingPolicy },
            }),
            invalidatesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<DefaultPricingPolicy>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandWhitelistedDomainNames: build.query<WhitelistedDomainName[], ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/whitelisted_customer_email_domain_names`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<{ domainNames: WhitelistedDomainName[] }>) =>
                response.data.domainNames,
            extraOptions: { maxRetries: 3 },
        }),
        updateBrandWhitelistedDomainNames: build.mutation<
            WhitelistedDomainName[],
            ApiRequest<UpdateWhitelistedDomainNamesPayload>
        >({
            query: ({ brandId, token, domainNames }) => ({
                url: `/brands/${brandId}/whitelisted_customer_email_domain_names`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { domainNames },
            }),
            invalidatesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<WhitelistedDomainName[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandIsWhitelistingEnabled: build.query<boolean, ApiRequest<GetBrandPayload>>({
            query: ({ brandId, token }) => ({
                url: `/brands/${brandId}/enable_customer_email_domain_names_whitelisting`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<{ isEnabled: boolean }>) => response.data.isEnabled,
            extraOptions: { maxRetries: 3 },
        }),
        updateBrandWhitelistingEnabled: build.mutation<boolean, ApiRequest<UpdateWhitelistingEnabledPayload>>({
            query: ({ brandId, token, isEnabled }) => ({
                url: `/brands/${brandId}/enable_customer_email_domain_names_whitelisting`,
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { isEnabled },
            }),
            invalidatesTags: [ApiTagTypes.Brand],
            transformResponse: (response: ApiResponse<{ isEnabled: boolean }>) => response.data.isEnabled,
            extraOptions: { maxRetries: 3 },
        }),
        getBrandRestaurantFamilies: build.query<BrandRestaurantFamily[], ApiRequest<{ brandId: number }>>({
            query: ({ brandId, token }) => ({
                url: `/brand/${brandId}/brand_restaurant_families`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [ApiTagTypes.BrandRestaurantFamilies],
            transformResponse: (response: ApiResponse<BrandRestaurantFamily[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        setBrandRestaurantFamilies: build.mutation<
            BrandRestaurantFamily[],
            ApiRequest<SetBrandRestaurantFamiliesPayload>
        >({
            query: ({ brandId, brandRestaurantFamilies, token }) => ({
                url: `/brand/${brandId}/brand_restaurant_families`,
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { brandRestaurantFamilies },
            }),
            invalidatesTags: [ApiTagTypes.BrandRestaurantFamilies],
            transformResponse: (response: ApiResponse<BrandRestaurantFamily[]>) => response.data,
        }),
        createBrandRestaurant: build.mutation<CreateBrandRestaurantResponse, ApiRequest<CreateBrandRestaurantPayload>>({
            query: ({ brandId, token, ...data }) => ({
                url: `/brand/${brandId}/restaurants`,
                method: 'POST',
                headers: {
                    authorization: `Bearer ${token}`,
                },
                body: { brandId, ...data },
            }),
            invalidatesTags: [ApiTagTypes.BrandRestaurants],
            transformResponse: (response: ApiResponse<CreateBrandRestaurantResponse>) => response.data,
        }),
    }),
});

export const {
    useGetActiveStudentsEntitiesQuery,
    useLazyGetActiveStudentsEntitiesQuery,
    useGetWhitelistedCustomerEmailDomainNamesQuery,
    useGetBrandQuery,
    useLazyGetBrandQuery,
    useGetBrandOperationZoneStatusQuery,
    useUpdateBrandMutation,
    useGetBrandEntranceFeesQuery,
    useGetBrandGrantsQuery,
    useGetBrandGroupsQuery,
    useGetBrandOptionsQuery,
    useGetBrandRestaurantsQuery,
    useGetGroupsMutation,
    useGetRestaurantsForBrandMutation,
    useUpdateBrandOptionsMutation,
    useLazyGetBrandPaymentConfigurationQuery,
    useUpdateBrandPaymentConfigurationMutation,
    useCountPostPaymentCustomersQuery,
    useLazyGetBrandsListV2Query,
    useToggleBrandFavoritesMutation,
    useCreateBrandMutation,
    useLazyGetBrandGrantsQuery,
    useLazyGetBrandEntranceFeesQuery,
    useLazyGetBrandOptInsQuery,
    useGetBrandOptInsQuery,
    useGetBrandModuleOrderingFlowQuery,
    useUpdateBrandModuleOrderingFlowMutation,
    useGetBrandDefaultPricingPolicyQuery,
    useLazyGetBrandDefaultPricingPolicyQuery,
    useUpdateBrandDefaultPricingPolicyMutation,
    useGetBrandWhitelistedDomainNamesQuery,
    useLazyGetBrandWhitelistedDomainNamesQuery,
    useUpdateBrandWhitelistedDomainNamesMutation,
    useGetBrandIsWhitelistingEnabledQuery,
    useLazyGetBrandIsWhitelistingEnabledQuery,
    useUpdateBrandWhitelistingEnabledMutation,
    useGetBrandRestaurantFamiliesQuery,
    useSetBrandRestaurantFamiliesMutation,
    useCreateBrandRestaurantMutation,
} = brandApi;
